import React, { useEffect, useRef, useState } from "react";
import { MoreVertical, Ban, Flag } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import BlockConfirmDialog from "@/components/BlockConfirmDialog";
import ReportDialog from "@/components/ReportDialog";

/**
 * OverflowMenu — discreet 3-dot menu with Block / Report.
 *
 * Props:
 *   userId: user_id of the other person
 *   contextMatchId?, contextMessageId?: pre-fill Report context
 *   onBlocked?(): callback after successful block (parent can redirect / hide UI)
 *   variant?: "dark" (light bg used on photo overlays) | default
 *   testIdPrefix?: string
 */
export default function OverflowMenu({
  userId,
  contextMatchId,
  contextMessageId,
  onBlocked,
  variant,
  testIdPrefix = "overflow",
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const doBlock = async () => {
    try {
      await api.post("/blocks", { target_user_id: userId });
      toast.success(t("app.moderation.blockDone"));
      setBlockOpen(false);
      if (onBlocked) onBlocked();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const isDark = variant === "dark";

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        aria-label={t("app.moderation.overflowMenu")}
        data-testid={`${testIdPrefix}-trigger`}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((v) => !v); }}
        className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
        style={{
          backgroundColor: isDark ? "rgba(255,255,255,0.15)" : "transparent",
          color: isDark ? "hsl(var(--nvl-bg))" : "hsl(var(--nvl-ink))",
        }}
      >
        <MoreVertical size={16} />
      </button>
      {open ? (
        <div
          className="absolute right-0 mt-1 min-w-[180px] rounded-md shadow-lg border py-1 z-40"
          style={{ backgroundColor: "hsl(var(--nvl-bg))", borderColor: "hsl(var(--nvl-border))" }}
          data-testid={`${testIdPrefix}-menu`}
          role="menu"
        >
          <button
            type="button"
            onClick={() => { setOpen(false); setReportOpen(true); }}
            className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[hsl(var(--muted))]"
            data-testid={`${testIdPrefix}-report`}
            role="menuitem"
          >
            <Flag size={14} /> {t("app.moderation.report")}
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); setBlockOpen(true); }}
            className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[hsl(var(--muted))]"
            style={{ color: "hsl(var(--destructive))" }}
            data-testid={`${testIdPrefix}-block`}
            role="menuitem"
          >
            <Ban size={14} /> {t("app.moderation.block")}
          </button>
        </div>
      ) : null}

      <BlockConfirmDialog
        open={blockOpen}
        onCancel={() => setBlockOpen(false)}
        onConfirm={doBlock}
      />
      <ReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        userId={userId}
        contextMatchId={contextMatchId}
        contextMessageId={contextMessageId}
      />
    </div>
  );
}
